import React from 'react';
import isEqual from 'react-fast-compare';
import _ from 'underscore';

import BenchlingConfig from 'BenchlingConfig';
import FileBrowserActions from 'file-browser/FileBrowserActions';
import ExpandedSidepanelThrobber from 'nav/expanded/ExpandedSidepanelThrobber';
import RequestAssignees from 'requests/assignees/RequestAssignees';
import {RequestSampleStatusCounts} from 'requests/fulfillments/FulfillmentConstants';
import {getLegacyRequestObjectName, Request, RequestSchema} from 'requests/RequestConstants';
import sortRequestTaskSchemas from 'requests/schemas/sortRequestTaskSchemas';
import * as ExpandedTableTagUtil from 'schemas/tags/ExpandedTableTagUtil';
import {Sort} from 'search/SearchConstants';
import {SearchResults, SortKey} from 'search/SearchConstants';
import {trackSelectedSearchResult} from 'search/searchTracking';
import {getColumnHeaderFromSortKey} from 'search/SortUtil';
import BenchlingIcon from 'ui-library/icons/BenchlingIcon';
import ExpandedScrollableTable from 'ui-library/scrollable/ExpandedScrollableTable';
import * as ScrollableTableConstants from 'ui-library/scrollable/ScrollableTableConstants';
import {
  computeStateForToggledRows,
  getColumnHeaderSortDirection,
} from 'ui-library/scrollable/ScrollableTableUtil';
import DateTableCell from 'ui-library/scrollable/table-cells/DateTableCell';
import {DatetimeTableCell} from 'ui-library/scrollable/table-cells/DatetimeTableCell';

import {RequestTaskSchemaGraphQL} from '../../../graphql-fragments/requests/schemas/__generated/RequestTaskSchemaGraphQL';
import RequestStatusLabel from './RequestStatusLabel';
import SampleStatusCounts from './SampleStatusCounts';

export function getColumns(
  sorts: Array<Sort>,
  omittedColumnKeys: Array<string> = []
): Array<ScrollableTableConstants.ScrollableTableColumn> {
  return [
    {
      accessorKey: 'name',
      header: `${getLegacyRequestObjectName()} ID`,
      flexGrow: 1,
      isFixed: true,
    },
    {
      accessorKey: 'status',
      header: 'Status',
      flexGrow: 1,
    },
    {
      accessorKey: 'sampleStatusCounts',
      header: 'Samples',
      flexGrow: 1,
    },
    {
      accessorKey: 'requestedTaskSchemas',
      header: 'Tasks',
      flexGrow: 1,
    },
    {
      accessorKey: `${BenchlingConfig.REQUESTS_ENABLE_REQUESTOR ? 'requestor' : 'creator'}`,
      header: `${BenchlingConfig.REQUESTS_ENABLE_REQUESTOR ? 'Requestor' : 'Creator'}`,
      flexGrow: 1,
    },
    {
      accessorKey: 'created',
      header: getColumnHeaderFromSortKey(SortKey.SORT_CREATED_AT_KEY),
      flexGrow: 1,
      isSortable: true,
      sortDirection: getColumnHeaderSortDirection(sorts, SortKey.SORT_CREATED_AT_KEY),
      sortKey: SortKey.SORT_CREATED_AT_KEY,
    },
    {
      accessorKey: 'assignees',
      header: 'Assigned To',
      flexGrow: 1,
    },
    {
      accessorKey: 'scheduledOn',
      header: getColumnHeaderFromSortKey(SortKey.SORT_SCHEDULED_ON_KEY),
      flexGrow: 1,
      isSortable: true,
      sortDirection: getColumnHeaderSortDirection(sorts, SortKey.SORT_SCHEDULED_ON_KEY),
      sortKey: SortKey.SORT_SCHEDULED_ON_KEY,
    },
  ].filter(({accessorKey}) => !omittedColumnKeys.includes(accessorKey));
}

interface RequestWithSchema extends Request {
  schema: RequestSchema;
}
interface RequestRow extends ScrollableTableConstants.ScrollableTableRow {
  extra: {request: Request};
}

function getOmittedColumnsKeys(requestsWithSchema: Array<RequestWithSchema>): Array<string> {
  const keys = [];

  const showSampleStatusCounts = requestsWithSchema.some(
    (request) => request.schema.sampleGroupSchema != null
  );
  if (!showSampleStatusCounts) {
    keys.push('sampleStatusCounts');
  }

  const showTasks = requestsWithSchema.some(
    (request) => request.schema && request.schema.requestTaskSchemaIds.length > 0
  );
  if (!showTasks) {
    keys.push('requestedTaskSchemas');
  }
  return keys;
}

export interface ExpandedRequestTableProps {
  isCompact: boolean;
  requests: Array<Request> | null;
  schemas: Array<RequestSchema> | null;
  emptyMessage: string;

  onUpdateSelectedRequestIds: (requestIds: Array<string>) => void;
  selectedRequestIds: Array<string>;

  sorts: Array<Sort>;
  onChangeSort: (sortKey: string) => void;

  requestTaskSchemasById: Record<string, RequestTaskSchemaGraphQL>;
  requestedTaskSchemaIdsByRequestId: {[requestId: string]: Array<string>};
  sampleStatusCountsByRequestId: {[requestId: string]: RequestSampleStatusCounts};

  searchResults?: SearchResults | null;
}

export class ExpandedRequestTable extends React.Component<ExpandedRequestTableProps> {
  override shouldComponentUpdate(nextProps: ExpandedRequestTableProps): boolean {
    return !isEqual(this.props, nextProps);
  }

  _handleToggleSelectRequests = (
    toggledRows: Array<ScrollableTableConstants.ScrollableTableRow>,
    isSelected: boolean
  ): void => {
    const newSelectedIdsState = computeStateForToggledRows({
      state: {selectedItemIds: this.props.selectedRequestIds},
      // eslint-disable-next-line no-restricted-syntax
      toggledIds: _.pluck(toggledRows, 'id'),
      isSelected,
      rowSelectionMode: ScrollableTableConstants.RowSelectionMode.MULTIPLE,
    });
    this.props.onUpdateSelectedRequestIds(newSelectedIdsState.selectedItemIds);
  };

  _hasAllRequestTaskSchemaIds(): boolean {
    if (this.props.requests == null) {
      return false;
    }

    return !this.props.requests.some(
      (request) => this.props.requestedTaskSchemaIdsByRequestId[request.id] == null
    );
  }

  override render(): JSX.Element {
    if (
      !this.props.requests ||
      !this.props.schemas ||
      !this.props.requestTaskSchemasById ||
      !this._hasAllRequestTaskSchemaIds()
    ) {
      return <ExpandedSidepanelThrobber />;
    }

    // The methods in ExpandedTableTagUtil expect all items to have a `schema` field, so we need
    // to add one to each request
    const requestsWithSchema: Array<RequestWithSchema> = this.props.requests
      .map((request: Request) => ({
        ...request,
        schema: _.findWhere(this.props.schemas!, {id: request.schemaId}),
      }))
      .filter(
        (requestWithOptionalSchema) => requestWithOptionalSchema.schema != null
      ) as Array<RequestWithSchema>;

    const tagColumns = ExpandedTableTagUtil.listSortedTagColumns(requestsWithSchema);
    const rows = requestsWithSchema.map((request) => {
      const {tagFields} = ExpandedTableTagUtil.mapTagsToColumns({tags: request.tags, tagColumns});
      const requestedTaskSchemas = sortRequestTaskSchemas(
        this.props.requestedTaskSchemaIdsByRequestId[request.id]
          .map((taskSchemaId: string) => this.props.requestTaskSchemasById[taskSchemaId])
          .filter((requestTaskSchema) => requestTaskSchema != null)
      );
      return {
        id: request.id,
        data: {
          name: request.name,
          assignees: <RequestAssignees assignees={request.assignees} />,
          // Show date with time in tooltip and date without time in cell.
          created: <DatetimeTableCell datetime={request.createdAt} showTimeInCell={false} />,
          creator: request.creator.name,
          requestor: request.requestor.name,
          scheduledOn: <DateTableCell date={request.scheduledOn} />,
          status: <RequestStatusLabel requestStatus={request.requestStatus} />,
          sampleStatusCounts: (
            <SampleStatusCounts statusCounts={this.props.sampleStatusCountsByRequestId[request.id]} />
          ),
          requestedTaskSchemas: requestedTaskSchemas.map((taskSchema) => taskSchema.name).join(', '),
          ...tagFields,
        },
        icon: <BenchlingIcon icon="request" className="scrollableTable-cell-icon" />,
        extra: {request},
        isSelected: this.props.selectedRequestIds.includes(request.id),
      };
    });
    const columns = getColumns(this.props.sorts, getOmittedColumnsKeys(requestsWithSchema)).concat(
      tagColumns
    );
    return (
      <ExpandedScrollableTable
        canFilterColumns={true}
        canResizeColumns={true}
        rowSelectionMode={ScrollableTableConstants.RowSelectionMode.MULTIPLE}
        className="u-flex-grow"
        columns={columns}
        emptyTableText={this.props.emptyMessage}
        getRowContentHeight={(rowIndex: number) => {
          if (rowIndex >= requestsWithSchema.length) {
            return ScrollableTableConstants.TABLE_ROW_BODY_HEIGHT;
          }
          return ExpandedTableTagUtil.getRowHeight(requestsWithSchema[rowIndex].tags);
        }}
        isCompact={this.props.isCompact}
        matchParentHeight={true}
        onChangeSort={this.props.onChangeSort}
        onRowClick={(row: ScrollableTableConstants.ScrollableTableRow) => {
          const requestId = (row as RequestRow).extra.request.id;
          trackSelectedSearchResult(requestId, this.props.searchResults);
          FileBrowserActions.openFile(requestId);
        }}
        onRowsSelect={this._handleToggleSelectRequests}
        rows={rows}
        tableKey={ScrollableTableConstants.TABLE_KEYS.EXPANDED_REQUEST_TABLE}
      />
    );
  }
}

export default ExpandedRequestTable;
